import { useEffect, useState } from 'react';
import type { CSSProperties, FormEvent, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Collection, ShareKind, Sweep } from '@vault/shared';
import { Badge, Button, EmptyState, Icon, Modal, Toast, Toggle } from '@vault/ui';
import type { ToastTone } from '@vault/ui';
import { api, ApiError } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.js';
import { useTheme } from '../theme/ThemeContext.js';
import { LOCALES, useT } from '../i18n/index.js';
import type { LocaleCode } from '../i18n/index.js';

const RULES_KEY = 'tv.captureRules';
const WEBSTORE_URL = 'https://chromewebstore.google.com/';

interface CaptureRules {
  saveContent: boolean;
  skipIncognito: boolean;
  blocklist: string[];
}
const DEFAULT_RULES: CaptureRules = {
  saveContent: true,
  skipIncognito: true,
  blocklist: ['mail.google.com', '*.bank.com'],
};

interface StoredShare {
  token: string;
  url: string;
  kind: ShareKind;
  targetId: string;
  label: string;
  createdAt: number;
}

function loadRules(): CaptureRules {
  try {
    const raw = localStorage.getItem(RULES_KEY);
    if (!raw) return DEFAULT_RULES;
    const parsed = JSON.parse(raw) as Partial<CaptureRules>;
    return {
      saveContent: parsed.saveContent ?? DEFAULT_RULES.saveContent,
      skipIncognito: parsed.skipIncognito ?? DEFAULT_RULES.skipIncognito,
      blocklist: Array.isArray(parsed.blocklist) ? parsed.blocklist : DEFAULT_RULES.blocklist,
    };
  } catch {
    return DEFAULT_RULES;
  }
}

function loadShares(email: string): StoredShare[] {
  try {
    const raw = localStorage.getItem(`tv.shares.${email}`);
    return raw ? (JSON.parse(raw) as StoredShare[]) : [];
  } catch {
    return [];
  }
}

/**
 * Settings hub: account & plan, connected browsers, on-device capture rules,
 * storage counts, public share links, appearance, and the danger zone.
 * Capture rules and the share-link list persist to localStorage (no API yet).
 */
export function SettingsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user, isPro, logout } = useAuth();
  const { theme, set: setTheme } = useTheme();
  const { t, locale, setLocale } = useT();
  const email = user?.email ?? '';

  const usage = useQuery({ queryKey: ['usage'], queryFn: () => api.account.usage() });
  const collections = useQuery({ queryKey: ['collections'], queryFn: () => api.collections.list() });
  const sweeps = useQuery({ queryKey: ['settings', 'sweeps'], queryFn: () => api.vault.sweeps({ limit: 25 }) });

  const [rules, setRules] = useState<CaptureRules>(loadRules);
  const [newSite, setNewSite] = useState('');
  const [shares, setShares] = useState<StoredShare[]>(() => loadShares(email));
  const [shareKind, setShareKind] = useState<ShareKind>('collection');
  const [shareTarget, setShareTarget] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [toast, setToast] = useState<{ tone: ToastTone; title: string; description?: string } | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem(RULES_KEY, JSON.stringify(rules));
    } catch {
      /* storage full / disabled — non-fatal */
    }
  }, [rules]);

  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 3400);
    return () => clearTimeout(id);
  }, [toast]);

  function persistShares(next: StoredShare[]) {
    setShares(next);
    try {
      localStorage.setItem(`tv.shares.${email}`, JSON.stringify(next));
    } catch {
      /* non-fatal */
    }
  }

  const createShare = useMutation({
    mutationFn: (v: { kind: ShareKind; targetId: string; label: string }) => api.share.create(v.kind, v.targetId),
    onSuccess: (data, v) => {
      persistShares([
        { token: data.share.token, url: data.share.url, kind: v.kind, targetId: v.targetId, label: v.label, createdAt: data.share.createdAt },
        ...shares,
      ]);
      setShareTarget('');
      setToast({ tone: 'success', title: t('settings.toast.linkCreated'), description: t('settings.toast.linkCreatedBody') });
    },
    onError: () => setToast({ tone: 'danger', title: t('settings.toast.linkFailed'), description: t('settings.toast.linkFailedBody') }),
  });

  const revokeShare = useMutation({
    mutationFn: (token: string) => api.share.revoke(token),
    onSuccess: (_data, token) => {
      persistShares(shares.filter((s) => s.token !== token));
      setToast({ tone: 'warn', title: t('settings.toast.linkRevoked'), description: t('settings.toast.linkRevokedBody') });
    },
    onError: (err, token) => {
      // 404 = already gone on the server; drop it locally so the list stays honest.
      if (err instanceof ApiError && err.status === 404) {
        persistShares(shares.filter((s) => s.token !== token));
        return;
      }
      setToast({ tone: 'danger', title: t('settings.toast.revokeFailed'), description: t('settings.toast.revokeFailedBody') });
    },
  });

  const deleteAccount = useMutation({
    mutationFn: () => api.account.deleteAccount(),
    onSuccess: async () => {
      setDeleteOpen(false);
      await logout();
      qc.clear();
      navigate('/login', { replace: true });
    },
    onError: () => setToast({ tone: 'danger', title: t('settings.toast.deleteFailed'), description: t('settings.toast.deleteFailedBody') }),
  });

  function addSite(e: FormEvent) {
    e.preventDefault();
    const site = newSite.trim().toLowerCase();
    if (!site || rules.blocklist.includes(site)) {
      setNewSite('');
      return;
    }
    setRules({ ...rules, blocklist: [...rules.blocklist, site] });
    setNewSite('');
  }

  function submitShare(e: FormEvent) {
    e.preventDefault();
    if (!shareTarget || createShare.isPending) return;
    const label =
      shareKind === 'collection'
        ? collections.data?.collections.find((c) => c.id === shareTarget)?.name ?? 'Collection'
        : sweepLabel(sweeps.data?.sweeps.find((s) => s.id === shareTarget));
    createShare.mutate({ kind: shareKind, targetId: shareTarget, label });
  }

  const counts = usage.data?.counts;
  const plan = user?.plan ?? usage.data?.plan ?? 'free';

  return (
    <div style={page}>
      <style>{SETTINGS_CSS}</style>
      <h1 style={heading}>{t('settings.title')}</h1>

      <div className="tv-set-wrap" style={{ display: 'flex', gap: 22, alignItems: 'flex-start' }}>
        {/* main column */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* account & sync */}
          <Section
            title={t('settings.section.account')}
            right={
              <span style={secureBadge}>
                <Icon name="lock" size={10} color="var(--success)" />
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, fontWeight: 600, color: 'var(--success)' }}>
                  {t('settings.account.private')}
                </span>
              </span>
            }
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px' }}>
              <span style={avatar}>{(email || '?').charAt(0).toUpperCase()}</span>
              <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                <span style={{ fontSize: 13.5, fontWeight: 600 }}>{email || t('settings.account.yourAccount')}</span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-3)' }}>
                  {plan === 'pro' ? t('settings.account.proPlan') : t('settings.account.freePlan')}
                </span>
              </span>
              <span style={{ marginInlineStart: 8 }}>
                <Badge tone={plan === 'pro' ? 'pro' : 'neutral'}>{plan === 'pro' ? 'PRO' : 'FREE'}</Badge>
              </span>
              <span style={{ marginInlineStart: 'auto' }}>
                <Button variant="ghost" size="sm" onClick={() => (isPro ? setPlanOpen(true) : navigate('/pricing'))}>
                  {isPro ? t('settings.account.managePlan') : t('settings.account.seePro')}
                </Button>
              </span>
            </div>
            <div style={{ padding: '0 16px 14px' }}>
              <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
                {t('settings.account.privacyNote')}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8, padding: '0 16px 14px' }}>
              <Button variant="ghost" size="sm" onClick={() => navigate('/billing')}>
                {t('settings.account.billing')}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => navigate('/settings/trash')}>
                {t('nav.trash')}
              </Button>
            </div>
          </Section>

          {/* connected browsers */}
          <Section
            title={`${t('settings.section.browsers')} · ${counts?.devices ?? 1}`}
            right={<span style={{ fontSize: 11, color: 'var(--text-3)' }}>{t('settings.browsers.syncAuto')}</span>}
          >
            <div style={{ ...listRow, borderBottom: 'none' }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--br-chrome)' }} />
              <span style={{ fontSize: 12.5, fontWeight: 500 }}>{t('settings.browsers.thisBrowser')}</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9.5, color: 'var(--text-3)' }}>{t('settings.browsers.webApp')}</span>
              <span style={{ marginInlineStart: 'auto', fontFamily: 'var(--font-mono)', fontSize: 9.5, color: 'var(--success)' }}>{t('settings.browsers.activeNow')}</span>
            </div>
            <div style={{ padding: '0 16px 13px' }}>
              <span style={{ fontSize: 11, color: 'var(--text-3)', lineHeight: 1.5 }}>
                {t('settings.browsers.installHint')}
              </span>
            </div>
          </Section>

          {/* capture rules */}
          <Section
            title={t('settings.section.capture')}
            right={<span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-3)' }}>{t('settings.capture.onDevice')}</span>}
          >
            <div style={{ ...listRow }}>
              <Toggle
                checked={rules.saveContent}
                onChange={(v) => setRules({ ...rules, saveContent: v })}
                label={t('settings.capture.saveContent')}
                description={t('settings.capture.saveContentDesc')}
              />
            </div>
            <div style={{ ...listRow }}>
              <Toggle
                checked={rules.skipIncognito}
                onChange={(v) => setRules({ ...rules, skipIncognito: v })}
                label={t('settings.capture.skipIncognito')}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '12px 16px 14px' }}>
              <span style={{ fontSize: 12.5, fontWeight: 500 }}>{t('settings.capture.neverOn')}</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {rules.blocklist.map((site) => (
                  <span key={site} style={siteChip}>
                    {site}
                    <span
                      onClick={() => setRules({ ...rules, blocklist: rules.blocklist.filter((s) => s !== site) })}
                      style={{ display: 'inline-flex', cursor: 'pointer' }}
                    >
                      <Icon name="x" size={9} color="var(--text-3)" />
                    </span>
                  </span>
                ))}
                <form onSubmit={addSite} style={{ display: 'inline-flex' }}>
                  <input
                    value={newSite}
                    onChange={(e) => setNewSite(e.target.value)}
                    placeholder={t('settings.capture.addSite')}
                    style={addSiteInput}
                  />
                </form>
              </div>
              <span style={{ fontSize: 10.5, color: 'var(--text-3)' }}>
                {t('settings.capture.localNote')}
              </span>
            </div>
          </Section>

          {/* sharing */}
          <Section
            title={t('settings.section.sharing')}
            right={<span style={{ fontSize: 11, color: 'var(--text-3)' }}>{t('settings.sharing.readOnly')}</span>}
          >
            <div style={{ padding: '13px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <form onSubmit={submitShare} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                <span style={segmented}>
                  <SegTab active={shareKind === 'collection'} onClick={() => { setShareKind('collection'); setShareTarget(''); }}>
                    {t('settings.sharing.collection')}
                  </SegTab>
                  <SegTab active={shareKind === 'sweep'} onClick={() => { setShareKind('sweep'); setShareTarget(''); }}>
                    {t('settings.sharing.sweep')}
                  </SegTab>
                </span>
                <select value={shareTarget} onChange={(e) => setShareTarget(e.target.value)} style={select}>
                  <option value="">
                    {shareKind === 'collection' ? t('settings.sharing.selectCollection') : t('settings.sharing.selectSweep')}
                  </option>
                  {shareKind === 'collection'
                    ? (collections.data?.collections ?? []).map((c: Collection) => (
                        <option key={c.id} value={c.id}>
                          {c.name} · {c.itemCount} tabs
                        </option>
                      ))
                    : (sweeps.data?.sweeps ?? []).map((s: Sweep) => (
                        <option key={s.id} value={s.id}>
                          {sweepLabel(s)} · {s.tabCount} tabs
                        </option>
                      ))}
                </select>
                <Button size="sm" loading={createShare.isPending} onClick={() => undefined} icon={<Icon name="share" size={13} color="currentColor" />}>
                  {t('settings.sharing.createLink')}
                </Button>
              </form>

              {shares.length === 0 ? (
                <EmptyState
                  dashed
                  icon={<Icon name="link" size={16} color="var(--text-3)" />}
                  title={t('settings.sharing.empty.title')}
                  description={t('settings.sharing.empty.body')}
                />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {shares.map((s) => (
                    <div key={s.token} style={shareRow}>
                      <Icon name="link" size={13} color="var(--accent-ink)" />
                      <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {s.label}
                        </span>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9.5, color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {s.url}
                        </span>
                      </span>
                      <span style={{ marginInlineStart: 'auto', display: 'inline-flex', gap: 6 }}>
                        <button
                          type="button"
                          className="tv-set-icon"
                          title={t('settings.sharing.copyLink')}
                          onClick={() => {
                            void navigator.clipboard?.writeText(s.url);
                            setToast({ tone: 'success', title: t('settings.toast.copied'), description: t('settings.toast.copiedBody') });
                          }}
                        >
                          <Icon name="external" size={12} />
                        </button>
                        <button
                          type="button"
                          className="tv-set-icon danger"
                          title={t('settings.sharing.revoke')}
                          onClick={() => revokeShare.mutate(s.token)}
                        >
                          <Icon name="trash" size={12} />
                        </button>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Section>

          {/* danger zone */}
          <div style={{ ...sectionShell, border: '1px solid var(--danger-line)' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 16 }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9.5, letterSpacing: '.14em', color: 'var(--danger)' }}>
                {t('settings.section.danger')}
              </span>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <a href={api.account.exportUrl()} download style={exportLink}>
                  <Icon name="download" size={13} color="currentColor" />
                  {t('settings.danger.export')}
                </a>
                <Button variant="danger" size="sm" onClick={() => setDeleteOpen(true)}>
                  {t('settings.danger.deleteAccount')}
                </Button>
              </div>
              <span style={{ fontSize: 10.5, color: 'var(--text-3)' }}>{t('settings.danger.note')}</span>
            </div>
          </div>
        </div>

        {/* right rail */}
        <div className="tv-set-rail" style={{ width: 300, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* storage */}
          <Section title={t('settings.section.storage')} right={<span style={{ fontFamily: 'var(--font-mono)', fontSize: 9.5, color: 'var(--text-2)' }}>{counts ? t('settings.storage.saved', { count: counts.items.toLocaleString() }) : '—'}</span>}>
            <div style={{ display: 'flex', gap: 10, padding: 14 }}>
              <Stat label={t('settings.storage.tabs')} value={counts?.items} />
              <span style={statDivider} />
              <Stat label={t('settings.storage.sessions')} value={counts?.sweeps} />
              <span style={statDivider} />
              <Stat label={t('settings.storage.indexed')} value={counts?.contents} accent />
            </div>
          </Section>

          {/* appearance */}
          <Section title={t('settings.section.appearance')}>
            <div style={{ ...listRow, borderBottom: 'none' }}>
              <Toggle
                checked={theme === 'dark'}
                onChange={(v) => setTheme(v ? 'dark' : 'light')}
                label={t('settings.appearance.dark')}
                description={t('settings.appearance.darkDesc')}
              />
            </div>
          </Section>

          {/* language */}
          <Section title={t('settings.section.language')}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '13px 16px 14px' }}>
              <span style={{ fontSize: 12.5, fontWeight: 500 }}>{t('settings.language.label')}</span>
              <select
                value={locale}
                onChange={(e) => setLocale(e.target.value as LocaleCode)}
                aria-label={t('settings.language.label')}
                style={{ ...select, width: '100%' }}
              >
                {LOCALES.map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.flag} {l.native} · {l.code.toUpperCase()}
                  </option>
                ))}
              </select>
              <span style={{ fontSize: 10.5, color: 'var(--text-3)' }}>{t('settings.language.desc')}</span>
            </div>
          </Section>

          {/* extension promo */}
          <div style={promoCard}>
            <span style={promoMark}>
              <Icon name="download" size={20} color="#fff" />
            </span>
            <span style={{ fontFamily: 'var(--font-display-cond)', fontSize: 17, fontWeight: 700, letterSpacing: '.02em', textTransform: 'uppercase', lineHeight: 1.25 }}>
              {t('settings.promo.title')}
            </span>
            <span style={{ fontSize: 12, lineHeight: 1.55, color: 'var(--text-2)' }}>
              {t('settings.promo.body')}
            </span>
            <a href={WEBSTORE_URL} target="_blank" rel="noopener noreferrer">
              <Button fullWidth icon={<Icon name="external" size={13} color="currentColor" />}>
                {t('common.addToChrome')}
              </Button>
            </a>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-3)', textAlign: 'center' }}>
              CHROME · EDGE · YANDEX · COMET
            </span>
          </div>

          {/* about */}
          <Section title={t('settings.section.about')}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 16 }}>
              <AboutRow k={t('settings.about.webApp')} v="2026.3.2" />
              <AboutRow k={t('settings.about.extension')} v="v2.4.1" />
            </div>
          </Section>
        </div>
      </div>

      {/* delete confirm */}
      <Modal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title={t('settings.deleteModal.title')}
        subtitle={t('settings.deleteModal.subtitle')}
        width={440}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleteOpen(false)}>
              {t('settings.deleteModal.keep')}
            </Button>
            <Button variant="danger" loading={deleteAccount.isPending} onClick={() => deleteAccount.mutate()}>
              {t('settings.deleteModal.confirm')}
            </Button>
          </>
        }
      >
        <span style={{ fontSize: 12.5, lineHeight: 1.6, color: 'var(--text-2)' }}>
          {t('settings.deleteModal.body')}
        </span>
      </Modal>

      {/* manage plan (pro) */}
      <Modal
        open={planOpen}
        onClose={() => setPlanOpen(false)}
        title={t('settings.planModal.title')}
        subtitle={t('settings.planModal.subtitle')}
        width={460}
        footer={
          <>
            <a href="mailto:support@tabs.nomadnet.ai?subject=Cancel%20Pro">
              <Button variant="ghost">{t('settings.planModal.emailSupport')}</Button>
            </a>
            <Button onClick={() => setPlanOpen(false)}>{t('settings.planModal.keepPro')}</Button>
          </>
        }
      >
        <span style={{ fontSize: 12.5, lineHeight: 1.6, color: 'var(--text-2)' }}>
          {t('settings.planModal.body')} <b style={{ color: 'var(--text-1)' }}>{t('settings.planModal.nothingDeleted')}</b>
        </span>
      </Modal>

      {toast && <Toast tone={toast.tone} title={toast.title} description={toast.description} />}
    </div>
  );
}

function Section({ title, right, children }: { title: string; right?: ReactNode; children: ReactNode }) {
  return (
    <div style={sectionShell}>
      <div style={sectionHead}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9.5, letterSpacing: '.14em', color: 'var(--text-3)' }}>{title}</span>
        {right && <span style={{ marginLeft: 'auto' }}>{right}</span>}
      </div>
      {children}
    </div>
  );
}

function SegTab({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        border: 'none',
        cursor: 'pointer',
        borderRadius: 7,
        padding: '6px 12px',
        fontFamily: 'var(--font-body)',
        fontSize: 12,
        fontWeight: active ? 600 : 500,
        color: active ? 'var(--text-1)' : 'var(--text-2)',
        background: active ? 'var(--surface-3)' : 'transparent',
      }}
    >
      {children}
    </button>
  );
}

function Stat({ label, value, accent }: { label: string; value?: number; accent?: boolean }) {
  return (
    <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, flex: 1 }}>
      <span style={{ fontFamily: 'var(--font-display-cond)', fontSize: 20, fontWeight: 700, color: accent ? 'var(--amber-ink)' : 'var(--text-1)' }}>
        {value == null ? '—' : value.toLocaleString()}
      </span>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 8, letterSpacing: '.1em', color: 'var(--text-3)' }}>{label}</span>
    </span>
  );
}

function AboutRow({ k, v }: { k: string; v: string }) {
  return (
    <span style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: 'var(--text-2)' }}>
      <span>{k}</span>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10 }}>{v}</span>
    </span>
  );
}

function sweepLabel(s?: Sweep): string {
  if (!s) return 'Sweep';
  if (s.title) return s.title;
  return new Date(s.clientCreatedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

const page: CSSProperties = { maxWidth: 1120, margin: '0 auto', padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 14 };
const heading: CSSProperties = {
  margin: 0,
  fontFamily: 'var(--font-display-cond)',
  fontSize: 22,
  fontWeight: 700,
  letterSpacing: '.02em',
  textTransform: 'uppercase',
};
const sectionShell: CSSProperties = { background: 'var(--surface-1)', border: '1px solid var(--line)', borderRadius: 14, overflow: 'hidden' };
const sectionHead: CSSProperties = { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderBottom: '1px solid var(--line)' };
const listRow: CSSProperties = { display: 'flex', alignItems: 'center', gap: 11, padding: '11px 16px', borderBottom: '1px solid var(--line)' };
const secureBadge: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'var(--success-tint)',
  border: '1px solid var(--success-line)',
  borderRadius: 999,
  padding: '4px 10px',
};
const avatar: CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: '50%',
  background: 'var(--grad-mark)',
  color: '#fff',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 14,
  fontWeight: 600,
  flexShrink: 0,
};
const siteChip: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  color: 'var(--text-2)',
  border: '1px solid var(--line-strong)',
  borderRadius: 7,
  padding: '4px 9px',
};
const addSiteInput: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  color: 'var(--text-1)',
  background: 'transparent',
  border: '1px dashed var(--line-strong)',
  borderRadius: 7,
  padding: '4px 9px',
  width: 110,
  outline: 'none',
};
const segmented: CSSProperties = { display: 'inline-flex', border: '1px solid var(--line-strong)', borderRadius: 9, padding: 2, gap: 2 };
const select: CSSProperties = {
  flex: 1,
  minWidth: 160,
  height: 34,
  padding: '0 10px',
  background: 'var(--bg)',
  color: 'var(--text-1)',
  border: '1px solid var(--line-strong)',
  borderRadius: 'var(--r-md)',
  fontFamily: 'var(--font-body)',
  fontSize: 12.5,
  outline: 'none',
};
const shareRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  background: 'var(--bg)',
  border: '1px solid var(--line)',
  borderRadius: 10,
  padding: '10px 12px',
};
const statDivider: CSSProperties = { width: 1, background: 'var(--line)' };
const exportLink: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  height: 30,
  padding: '0 12px',
  borderRadius: 'var(--r-md)',
  border: '1px solid var(--line-strong)',
  background: 'transparent',
  color: 'var(--text-1)',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};
const promoCard: CSSProperties = {
  borderRadius: 16,
  overflow: 'hidden',
  border: '1px solid var(--line-primary)',
  background: 'linear-gradient(160deg, rgba(59,107,255,.20), transparent 55%), var(--surface-1)',
  padding: 20,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};
const promoMark: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 44,
  height: 44,
  borderRadius: 13,
  background: 'var(--grad-mark)',
};

const SETTINGS_CSS = `
.tv-set-icon{display:inline-flex;align-items:center;justify-content:center;width:26px;height:26px;border-radius:7px;
  border:1px solid var(--line-strong);background:transparent;color:var(--text-2);cursor:pointer}
.tv-set-icon:hover{background:var(--surface-2);color:var(--text-1)}
.tv-set-icon.danger:hover{background:var(--danger-tint);color:var(--danger);border-color:var(--danger-line)}
@media (max-width:900px){.tv-set-wrap{flex-direction:column}.tv-set-rail{width:100% !important}}
`;
